'use client';

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import { optimisticallyMarkConversationRead } from '../lib/messagingQueryCache';
import { CONVERSATION_DETAIL_POLL_MS } from '../lib/pollingConstants';
import { resolvePollIntervalMs, type PollingEndpointKey } from '../lib/pollingManager';
import { usePollingGate } from './usePollingGate';

const ENDPOINT_KEY: PollingEndpointKey = 'conversation-detail';

type Options = {
  conversationId: number | null | undefined;
  enabled?: boolean;
  poll?: boolean;
  messageLimit?: number;
  /** false = ne pas marquer lu (ex. après « marquer non lu »). Défaut true. */
  markRead?: boolean;
};

export function useConversationDetailQuery(options: Options) {
  const {
    conversationId,
    enabled = true,
    poll = false,
    messageLimit = 100,
    markRead = true,
  } = options;
  const queryClient = useQueryClient();
  const { allowPolling } = usePollingGate();
  const id = conversationId ?? null;
  const isValidId = id != null && !Number.isNaN(id) && id > 0;

  // Dès l’ouverture : badge / compteurs sans attendre le poll de la liste.
  useEffect(() => {
    if (!enabled || !isValidId || !markRead || id == null) {
      return;
    }
    optimisticallyMarkConversationRead(queryClient, id);
  }, [enabled, isValidId, id, markRead, queryClient]);

  return useQuery({
    queryKey: ['conversation', id],
    queryFn: () => apiService.getConversation(id!, 1, messageLimit, { markRead }),
    enabled: enabled && isValidId,
    retry: 0,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchInterval: resolvePollIntervalMs(
      poll && isValidId ? CONVERSATION_DETAIL_POLL_MS : false,
      ENDPOINT_KEY,
      allowPolling,
    ),
    meta: { pollingEndpoint: ENDPOINT_KEY },
  });
}
